import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, Clock3 } from "lucide-react";
import { Reveal } from "../components/Reveal";

const VIDEO_URL =
  "https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/Comp-3.mp4";

const WATCH_URL =
  "https://dorado9.com/watch/dorado9-film-001";

const VIDEO_TITLE =
  "Dorado9 Film 001: Make Them Look Twice";

const VIDEO_DESCRIPTION =
  "A visual film from Dorado9 exploring strategy, creativity and the ideas behind the work we create for ambitious businesses. Dorado9 is a digital marketing and creative agency based in Amritsar, Punjab, serving businesses across India and global markets.";

const UPLOAD_DATE = "2026-08-01";

const VIDEO_DURATION = "PT1M00S";

export default function WatchVideo() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    const playVideo = () => video.play().catch(() => {});

    if (video.readyState >= 2) {
      playVideo();
    } else {
      video.addEventListener("loadeddata", playVideo, { once: true });
    }

    return () => video.removeEventListener("loadeddata", playVideo);
  }, []);

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: VIDEO_TITLE,
    description: VIDEO_DESCRIPTION,
    uploadDate: UPLOAD_DATE,
    duration: VIDEO_DURATION,
    contentUrl: VIDEO_URL,
    embedUrl: WATCH_URL,
    url: WATCH_URL,
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Dorado9",
      url: "https://dorado9.com",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://dorado9.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Watch",
        item: "https://dorado9.com/watch/dorado9-film-001",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: VIDEO_TITLE,
        item: WATCH_URL,
      },
    ],
  };

  return (
    <>
      {/* VIDEO STRUCTURED DATA */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(videoSchema),
        }}
      />

      {/* BREADCRUMB STRUCTURED DATA */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />

      {/* HERO / VIDEO */}
      <section className="bg-brand-bg pt-36 lg:pt-48 pb-16 lg:pb-20 px-6 lg:px-12">

        <div className="max-w-[1400px] mx-auto">

          {/* BREADCRUMB */}
          <Reveal>
            <div className="mb-10">

              <Link
                to="/"
                className="
                  inline-flex
                  items-center
                  gap-2
                  text-[10px]
                  uppercase
                  tracking-[0.24em]
                  text-zinc-500
                  hover:text-brand-mint
                  transition-colors
                "
              >
                Dorado9
              </Link>

              <span className="mx-3 text-zinc-700">
                /
              </span>

              <span className="
                text-[10px]
                uppercase
                tracking-[0.24em]
                text-zinc-600
              ">
                Watch
              </span>

            </div>
          </Reveal>


          {/* EYEBROW */}
          <Reveal delay={0.05}>
            <p className="eyebrow mb-6 flex items-center gap-3">

              <span className="w-8 h-px bg-brand-purple" />

              Dorado9 Film 001

            </p>
          </Reveal>


          {/* TITLE */}
          <Reveal delay={0.1}>

            <h1
              className="
                font-display
                text-5xl
                sm:text-6xl
                lg:text-[7vw]
                leading-[0.88]
                tracking-tighter
                uppercase
                max-w-6xl
              "
            >
              Make Them{" "}
              <span className="font-serif-accent text-brand-mint lowercase">
                look twice.
              </span>
            </h1>

          </Reveal>


          {/* DESCRIPTION */}
          <Reveal delay={0.15}>

            <div className="
              mt-8
              max-w-2xl
              text-zinc-400
              text-base
              lg:text-lg
              leading-8
            ">
              A visual film from Dorado9 exploring strategy,
              creativity and the ideas behind the work we create
              for ambitious businesses.
            </div>

          </Reveal>


          {/* VIDEO */}
          <Reveal delay={0.2}>

            <div
              className="
                relative
                mt-14
                lg:mt-20
                w-full
                aspect-video
                overflow-hidden
                border
                border-white/[0.08]
                bg-[#151515]
                shadow-[0_30px_100px_rgba(0,0,0,.35)]
              "
            >

              <video
                ref={videoRef}
                className="
                  absolute
                  inset-0
                  w-full
                  h-full
                  object-cover
                "
                autoPlay
                muted
                playsInline
                controls
                preload="metadata"
              >
                <source
                  src={VIDEO_URL}
                  type="video/mp4"
                />

                Your browser does not support the video
                element.
              </video>

            </div>

          </Reveal>


          {/* VIDEO META */}
          <Reveal delay={0.25}>

            <div
              className="
                mt-6
                flex
                flex-wrap
                items-center
                justify-between
                gap-5
                border-b
                border-white/[0.07]
                pb-6
              "
            >

              <div className="flex items-center gap-6">

                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-brand-mint">
                  <Play size={12} />
                  Dorado9 Film
                </span>

                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <Clock3 size={12} />
                  Film
                </span>

              </div>


              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                Amritsar → Worldwide
              </span>

            </div>

          </Reveal>

        </div>

      </section>


      {/* ABOUT THE FILM */}
      <section
        className="
          bg-brand-bg
          py-24
          lg:py-32
          px-6
          lg:px-12
          border-t
          border-zinc-900
        "
      >

        <div className="
          max-w-[1400px]
          mx-auto
          grid
          lg:grid-cols-12
          gap-12
          lg:gap-20
        ">

          {/* LEFT */}
          <div className="lg:col-span-4">

            <Reveal>

              <p className="eyebrow mb-6">
                About the film
              </p>

              <h2
                className="
                  font-display
                  text-3xl
                  lg:text-5xl
                  uppercase
                  tracking-tight
                  leading-[0.95]
                "
              >
                Strategy.
                <br />

                <span className="font-serif-accent text-brand-mint lowercase">
                  Creativity.
                </span>
                <br />

                Just enough chaos.
              </h2>

            </Reveal>

          </div>


          {/* RIGHT */}
          <div className="lg:col-span-8">

            <Reveal delay={0.1}>

              <div className="
                max-w-3xl
                text-zinc-400
                text-base
                lg:text-lg
                leading-8
              ">

                <p>
                  Dorado9 combines digital marketing, creative
                  strategy, technology and design to help businesses
                  build stronger digital experiences.
                </p>

                <p className="mt-6">
                  This film is a glimpse into the creative thinking
                  behind our work and the way we approach digital
                  growth for ambitious brands.
                </p>

                <p className="mt-6">
                  Based in Amritsar, Punjab, Dorado9 works with
                  businesses across India and international markets.
                </p>

              </div>

            </Reveal>

          </div>

        </div>

      </section>


      {/* EXPLORE */}
      <section
        className="
          bg-brand-bg
          py-24
          lg:py-32
          px-6
          lg:px-12
          border-t
          border-zinc-900
        "
      >

        <div className="max-w-[1400px] mx-auto">

          <Reveal>

            <p className="eyebrow mb-6">
              Explore Dorado9
            </p>

            <h2
              className="
                font-display
                text-4xl
                lg:text-6xl
                uppercase
                tracking-tighter
                leading-[0.9]
                max-w-4xl
              "
            >
              More than just{" "}
              <span className="font-serif-accent text-brand-mint lowercase">
                marketing.
              </span>
            </h2>

          </Reveal>


          <div className="
            mt-14
            grid
            md:grid-cols-3
            gap-6
          ">

            <ExploreLink
              to="/services"
              title="Our Services"
              text="Explore SEO, paid media, social media, web and creative services."
            />

            <ExploreLink
              to="/portfolio"
              title="Our Work"
              text="See websites, campaigns, branding and digital experiences we've built."
            />

            <ExploreLink
              to="/contact"
              title="Start a Project"
              text="Have something in mind? Tell us what you're working on."
            />

          </div>

        </div>

      </section>


      {/* FINAL CTA */}
      <section
        className="
          relative
          overflow-hidden
          bg-brand-bg
          py-28
          lg:py-40
          px-6
          lg:px-12
          border-t
          border-zinc-900
        "
      >

        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] h-[500px] rounded-full blur-[170px] opacity-[0.16] pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(102,76,221,.9), transparent 68%)",
          }}
        />

        <div
          className="absolute right-[10%] bottom-[-180px] w-[420px] h-[420px] rounded-full blur-[160px] opacity-[0.07] pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(17,245,186,.8), transparent 70%)",
          }}
        />

        <div className="relative z-10 max-w-[1500px] mx-auto text-center">
          <Reveal>
            <p className="eyebrow mb-8 text-brand-mint">
              YOUR NEXT MOVE STARTS HERE
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-[7vw] leading-[0.88] tracking-tighter uppercase text-white">
              YOU'VE SCROLLED
              <br />
              <span className="font-serif-accent text-brand-mint lowercase">
                this far.
              </span>
            </h2>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="mt-7 text-zinc-400 text-base lg:text-lg leading-8 max-w-xl mx-auto">
              You might as well talk.
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="mt-12 flex justify-center">
              <Link
                to="/contact"
                className="group relative inline-flex items-center gap-5 rounded-full bg-brand-purple px-9 py-5 text-base lg:text-lg font-semibold text-white overflow-hidden transition-all duration-500 hover:scale-[1.03] shadow-[0_0_18px_rgba(102,76,221,0.45),0_0_45px_rgba(102,76,221,0.25)] hover:shadow-[0_0_22px_rgba(102,76,221,0.65),0_0_60px_rgba(102,76,221,0.35)]"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.12] to-transparent skew-x-[-20deg] transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative z-10">Let's make something happen</span>
                <span className="relative z-10 flex h-7 w-7 items-center justify-center transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1">
                  <ArrowUpRight size={21} strokeWidth={2.2} />
                </span>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.32}>
            <div className="mt-10 flex items-center justify-center gap-3 text-[9px] uppercase tracking-[0.3em] text-zinc-600">
              <span className="w-8 h-px bg-zinc-800" />
              No pressure. Just a conversation.
              <span className="w-8 h-px bg-zinc-800" />
            </div>
          </Reveal>
        </div>

      </section>
    </>
  );
}


function ExploreLink({ to, title, text }) {

  return (
    <Link
      to={to}
      className="
        group
        block
        border
        border-white/[0.06]
        bg-white/[0.015]
        p-8
        lg:p-10
        min-h-[220px]
        transition-all
        duration-500
        hover:border-brand-mint/30
      "
    >

      <div className="
        flex
        items-start
        justify-between
        gap-5
      ">

        <h3
          className="
            font-display
            text-2xl
            lg:text-3xl
            uppercase
            tracking-tight
            group-hover:text-brand-mint
            transition-colors
          "
        >
          {title}
        </h3>

        <ArrowUpRight
          size={20}
          className="
            text-zinc-600
            group-hover:text-brand-mint
            transition-all
            duration-300
            group-hover:translate-x-1
            group-hover:-translate-y-1
          "
        />

      </div>

      <p className="
        mt-8
        text-zinc-500
        leading-7
        max-w-sm
      ">
        {text}
      </p>

    </Link>
  );
}